import { z } from 'zod';

/** Validates yyyy-MM-dd is a real calendar date. No luxon dependency. Exported for reuse in UI validation (e.g. date-of-birth field). */
export function isValidDateString(value: string): boolean {
  if (!value || value.length < 10) return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (month < 1 || month > 12) return false;
  const lastDay = new Date(year, month, 0).getDate();
  return day >= 1 && day <= lastDay;
}

export interface ConsentLinkDto {
  id: number;
  key: string;
  userId: string;
  expiration: number;
  url: string;
}

export interface ConsentLinkRefreshRequestDto {
  key: string;
}

export interface TermIdentityDto {
  id: string;
  createdAt: number;
}
export interface TermResponseSubmissionDto {
  id: number;
  userId: string;
  consentLinkId: number;
  internalSubmitterUserId: string;
  createdAt: number;
  termIdentityId: number;
  ipAddress: string;
  languageCode: string;
}

export interface TermResponseDto {
  termId: number;
  consented: boolean;
  consentIdentityId?: string;
}

/**
 * One historical accept/reject for a consent term, including submission metadata
 * used by Care Portal consent history.
 */
export interface TermHistoryEntryDto {
  id: number;
  termId: number;
  consented: boolean;
  termResponseSubmission?: TermResponseSubmissionDto;
}

export interface TermDto {
  id: number;
  key: string;
  name: string;
  shortName: string;
  description: string;
  infoText: string;
  text: string;
  acceptText: string;
  rejectText: string;
  internalNonConsentLimitation: string;
  createdAt?: number;
  identityRequired: boolean;
  consented?: boolean;
  termResponseSubmission?: TermResponseSubmissionDto;
  history?: TermHistoryEntryDto[];
  /**
   * True when this term is no longer on the client's current groups but the
   * member has prior signatures. Guide history only — not signable.
   */
  historyOnly?: boolean;
  clientConfiguration: JSON;
}

export interface TermContentDto {
  id: number;
  key: string;
  content: string;
  infoContent?: string;
}

export interface TermGroupDto {
  id: number;
  clientId: number;
  key: string;
  name: string;
  userHeader: string;
  userOverview: string;
  terms: TermDto[];
  createdAt: number;
}

export interface ConsentGroupsDto {
  termGroups: TermGroupDto[];
}

export interface ConsentGoalDto {
  id: number;
  key: string;
  name: string;
  description: string;
  goalType: string;
  consented: boolean;
  completed: boolean;
  completedAt: number;
  memberId: string;
  term: TermDto;
}

export const ConsentIdentitySchema = z
  .object({
    name: z.string().min(2, { message: 'validation.required' }),
    dateOfBirth: z.string().min(8, { message: 'validation.required' }),
    /** Empty string is valid (optional field); non-empty must be valid email. */
    email: z.union([z.literal(''), z.string().email()]).optional(),
    line1: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    consentLinkKey: z.string().optional(),
  })
  .strict()
  .refine(
    data => isValidDateString(data.dateOfBirth ?? ''),
    { message: 'validation.invalidDateOfBirth', path: ['dateOfBirth'] }
  );

export type ConsentIdentityFormValues = z.infer<typeof ConsentIdentitySchema>;

export const ConsentFormSchema = z
  .object({
    consentLinkKey: z.string().optional(),
    consentIdentityId: z.string().optional(),
    termId: z.number(),
    consented: z.coerce.boolean(),
  })
  .strict();
